import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, ClipboardText, MagnifyingGlass, ClockCounterClockwise, Gear, CheckCircle, Warning, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi, WO_STATUSES, WO_STATUS_BADGE } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import WorkOrderFormDialog from './WorkOrderFormDialog';
import WorkOrderDetailDialog from './WorkOrderDetailDialog';

function Stat({ Icon, label, value, tone, testid }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${tone} mb-2`}><Icon size={18} weight="duotone" /></div>
      <div className="text-2xl font-bold text-zinc-900 tabular-nums">{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function WorkOrders() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editWo, setEditWo] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [delWo, setDelWo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [autoReplenish, setAutoReplenish] = useState(null);

  useEffect(() => { mfgApi.woSettings().then((s) => setAutoReplenish(!!s.auto_replenish)).catch(() => {}); }, []);
  const toggleReplenish = async (v) => {
    setAutoReplenish(v);
    try { await mfgApi.saveWoSettings({ auto_replenish: v }); toast.success(t('mfg.common.saved')); }
    catch (e) { setAutoReplenish(!v); toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };

  const load = useCallback(() => {
    const params = {};
    if (status !== 'all') params.status = status;
    if (q.trim()) params.q = q.trim();
    mfgApi.workOrders(params).then(setList).catch(() => {});
    mfgApi.workOrderStats().then(setStats).catch(() => {});
  }, [status, q]);
  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);

  const doDelete = async () => {
    if (!delWo) return;
    setDeleting(true);
    try {
      await mfgApi.deleteWorkOrder(delWo.id);
      toast.success(t('mfg.wo.deleted')); setDelWo(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setDeleting(false); }
  };
  const canDelete = (w) => canDeleteMfg && ['planned', 'cancelled'].includes(w.status);

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-work-orders">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.wo.title')}</h1>
        {canWrite && (
          <Button data-testid="wo-new-btn" onClick={() => { setEditWo(null); setFormOpen(true); }} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> {t('mfg.wo.new')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat Icon={ClipboardText} label={t('mfg.wo.stat.planned')} value={stats?.by_status?.planned ?? 0} tone="bg-zinc-100 text-zinc-600" testid="wo-stat-planned" />
        <Stat Icon={ClockCounterClockwise} label={t('mfg.wo.stat.released')} value={stats?.by_status?.released ?? 0} tone="bg-blue-50 text-blue-600" testid="wo-stat-released" />
        <Stat Icon={Gear} label={t('mfg.wo.stat.in_progress')} value={stats?.by_status?.in_progress ?? 0} tone="bg-amber-50 text-amber-600" testid="wo-stat-inprogress" />
        <Stat Icon={CheckCircle} label={t('mfg.wo.stat.completed')} value={stats?.by_status?.completed ?? 0} tone="bg-emerald-50 text-emerald-600" testid="wo-stat-completed" />
        <Stat Icon={Warning} label={t('mfg.wo.stat.shortage')} value={stats?.with_shortage ?? 0} tone="bg-rose-50 text-rose-600" testid="wo-stat-shortage" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {['all', ...WO_STATUSES].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`wo-status-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${status === s ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {s === 'all' ? t('mfg.common.all') : t(`mfg.wo.status.${s}`)}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs flex-1 min-w-[160px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="wo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mfg.common.search')} className="h-10 pl-9 rounded-lg" />
        </div>
        {autoReplenish !== null && (
          <label className="flex items-center gap-2 ml-auto rounded-full border border-zinc-200 bg-white px-3 py-1.5 cursor-pointer" title={t('mfg.wo.autoReplenishHint')} data-testid="wo-auto-replenish-toggle">
            <Switch checked={autoReplenish} onCheckedChange={toggleReplenish} disabled={!canWrite} data-testid="wo-auto-replenish-switch" />
            <span className="text-xs font-medium text-zinc-600">{t('mfg.wo.autoReplenish')}</span>
          </label>
        )}
      </div>

      <DataTable
        dataTestId="wo-table"
        rows={list}
        rowKey={(w) => w.id}
        rowTestId={(w) => `wo-row-${w.id}`}
        onRowClick={(w) => setDetailId(w.id)}
        empty={{ icon: <ClipboardText size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.wo.empty'), testid: 'wo-empty' }}
        columns={[
          { key: 'wo_no', header: t('mfg.wo.no'), cell: (w) => <span className="font-mono text-xs text-zinc-600">{w.wo_no}{w.manufacturing_no && <span className="block text-[11px] text-orange-600 font-semibold" data-testid={`wo-mfgno-${w.id}`}>{w.manufacturing_no}</span>}</span> },
          { key: 'product', primary: true, header: t('mfg.wo.product'), cell: (w) => <span><span className="font-medium text-zinc-800"><span data-no-translate>{w.product_name}</span></span>{w.product_sku && <span className="ml-1.5 font-mono text-[11px] text-zinc-400">{w.product_sku}</span>}{w.has_shortage && <Badge variant="outline" className="ml-2 bg-rose-50 text-rose-700 border-rose-200" data-testid={`wo-shortage-badge-${w.id}`}>{t('mfg.wo.shortage')}</Badge>}</span> },
          { key: 'qty', header: t('mfg.wo.qty'), align: 'right', cell: (w) => <span className="tabular-nums text-zinc-700">{w.qty} {w.product_uom || ''}</span> },
          { key: 'due', header: t('mfg.wo.dueDate'), cell: (w) => <span className="text-zinc-600">{w.due_date || '—'}{w.overdue && <Badge variant="outline" className="ml-2 bg-orange-50 text-orange-700 border-orange-200">{t('mfg.wo.overdue')}</Badge>}</span> },
          { key: 'status', header: '', cell: (w) => <Badge variant="outline" className={WO_STATUS_BADGE[w.status]}>{w.status_label}</Badge> },
          { key: 'actions', actions: true, header: '', cell: (w) => (
            <div className="flex items-center justify-end gap-1.5">
              <Button size="sm" variant="outline" className="h-9" data-testid={`wo-view-${w.id}`} onClick={(ev) => { ev.stopPropagation(); setDetailId(w.id); }}>{t('mfg.wo.view')}</Button>
              {canDelete(w) && <Button size="sm" variant="outline" className="h-9 w-9 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`wo-delete-${w.id}`} onClick={(ev) => { ev.stopPropagation(); setDelWo(w); }}><Trash size={15} /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!delWo} onOpenChange={(o) => { if (!o) setDelWo(null); }}>
        <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="wo-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-900"><Trash size={18} weight="fill" className="text-rose-500" />{t('mfg.wo.deleteTitle')}</DialogTitle>
            <DialogDescription className="text-zinc-500">{t('mfg.wo.deleteConfirm')}{delWo?.wo_no ? ` (${delWo.wo_no})` : ''}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" data-testid="wo-delete-cancel" onClick={() => setDelWo(null)}>{t('mfg.cancel')}</Button>
            <Button variant="destructive" data-testid="wo-delete-confirm" onClick={doDelete} disabled={deleting}>{deleting ? t('mfg.saving') : t('mfg.wo.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WorkOrderFormDialog open={formOpen} workOrder={editWo}
        onClose={() => { setFormOpen(false); setEditWo(null); }}
        onSaved={() => { setFormOpen(false); setEditWo(null); load(); }} />
      <WorkOrderDetailDialog workOrderId={detailId} onClose={() => setDetailId(null)} onChanged={load}
        onEdit={(w) => { setDetailId(null); setEditWo(w); setFormOpen(true); }} />
    </div>
  );
}
